import { useState, useEffect } from 'react';
import { Chat } from './components/Chat';
import { Voice } from './components/Voice';
import { Quiz } from './components/Quiz';
import { Progress } from './components/Progress';

type Mode = 'chat' | 'voice' | 'quiz' | 'progress';

function App() {
  const [userId, setUserId] = useState('');
  const [topic, setTopic] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('chat');
  const [isSetupComplete, setIsSetupComplete] = useState(false);

  useEffect(() => {
    // Check if user has already set up
    const savedUserId = localStorage.getItem('userId');
    const savedTopic = localStorage.getItem('topic');

    if (savedUserId && savedTopic) {
      setUserId(savedUserId);
      setTopic(savedTopic);
      setIsSetupComplete(true);
    }
  }, []);

  const handleSetup = (e: React.FormEvent) => {
    e.preventDefault();
    if (userId.trim() && topic.trim()) {
      localStorage.setItem('userId', userId);
      localStorage.setItem('topic', topic);
      setIsSetupComplete(true);
    }
  };

  const handleReset = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('topic');
    setUserId('');
    setTopic('');
    setSessionId(null);
    setIsSetupComplete(false);
    setMode('chat');
  };

  if (!isSetupComplete) {
    return (
      <div style={styles.setupContainer}>
        <div style={styles.setupCard}>
          <h1 style={styles.setupTitle}>Welcome to AI Study Buddy</h1>
          <p style={styles.setupSubtitle}>Let's get started with your learning journey!</p>

          <form onSubmit={handleSetup} style={styles.setupForm}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Name or ID</label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="e.g., John Doe"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>What do you want to study?</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., JavaScript, Biology, Spanish"
                style={styles.input}
                required
              />
            </div>

            <button type="submit" style={styles.setupButton}>
              Start Learning
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.appContainer}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.appTitle}>AI Study Buddy</h1>
          <div style={styles.headerInfo}>
            <span style={styles.userInfo}>
              {userId} | Studying: {topic}
            </span>
            <button onClick={handleReset} style={styles.resetButton}>
              Change Topic
            </button>
          </div>
        </div>
      </header>

      <nav style={styles.nav}>
        <button
          onClick={() => setMode('chat')}
          style={{
            ...styles.navButton,
            ...(mode === 'chat' ? styles.navButtonActive : {}),
          }}
        >
          💬 Chat
        </button>
        <button
          onClick={() => setMode('voice')}
          style={{
            ...styles.navButton,
            ...(mode === 'voice' ? styles.navButtonActive : {}),
          }}
        >
          🎤 Voice
        </button>
        <button
          onClick={() => setMode('quiz')}
          style={{
            ...styles.navButton,
            ...(mode === 'quiz' ? styles.navButtonActive : {}),
          }}
        >
          📝 Quiz
        </button>
        <button
          onClick={() => setMode('progress')}
          style={{
            ...styles.navButton,
            ...(mode === 'progress' ? styles.navButtonActive : {}),
          }}
        >
          📊 Progress
        </button>
      </nav>

      <main style={styles.main}>
        {mode === 'chat' && (
          <Chat
            userId={userId}
            topic={topic}
            sessionId={sessionId}
            onSessionIdChange={setSessionId}
          />
        )}
        {mode === 'voice' && (
          <Voice
            userId={userId}
            topic={topic}
            sessionId={sessionId}
            onSessionIdChange={setSessionId}
          />
        )}
        {mode === 'quiz' && <Quiz userId={userId} topic={topic} />}
        {mode === 'progress' && <Progress userId={userId} />}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  setupContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f2f5',
    padding: '20px',
  },
  setupCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '48px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  setupTitle: {
    textAlign: 'center',
    marginBottom: '12px',
    color: '#333',
  },
  setupSubtitle: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '32px',
  },
  setupForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
  },
  setupButton: {
    padding: '16px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  appContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  header: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '16px 24px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  appTitle: {
    margin: 0,
    fontSize: '24px',
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userInfo: {
    fontSize: '14px',
  },
  resetButton: {
    padding: '8px 16px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: '1px solid white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  nav: {
    display: 'flex',
    backgroundColor: 'white',
    borderBottom: '1px solid #ddd',
    padding: '0 24px',
  },
  navButton: {
    padding: '16px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '16px',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
  },
  navButtonActive: {
    borderBottomColor: '#007bff',
    color: '#007bff',
    fontWeight: 'bold',
  },
  main: {
    flex: 1,
    overflow: 'auto',
  },
};

export default App;
